(() => {
  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };
  const track = (event, data = {}) => document.dispatchEvent(new CustomEvent('zyma:track', { detail: { event, data } }));
  const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (match, key) => key in values ? values[key] : match);
  const init = async (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.zymaBuilderReady === 'true') return;
    root.dataset.zymaBuilderReady = 'true';
    const text = name => root.dataset['text' + name] || '';
    // Inline, dependency-free quote path: an external module failure must never block Add to Bag.
    const quoteProduct=(unitPrice,quantity,rules=[])=>{
      const count=Math.max(0,Math.round(Number(quantity)||0)),subtotal=Math.max(0,Math.round(Number(unitPrice)||0))*count;
      const rule=rules.filter(r=>count>=Number(r.min)&&(r.max==null||count<=Number(r.max))).sort((a,b)=>Number(b.min)-Number(a.min))[0];
      let saving=0;
      if(rule?.type==='fixed_amount')saving=Math.round(Number(rule.value)*100);
      else if(rule?.type==='percentage')saving=Math.round(subtotal*Number(rule.value)/100);
      else if(rule?.type==='fixed_price')saving=subtotal-Math.round(Number(rule.value)*100);
      return Math.max(0,Math.min(subtotal,Number.isFinite(saving)?saving:0));
    };
    const cartQuote=(states,priceRules,mixed)=>{
      let subtotal=0,eligibleCount=0,eligibleSubtotal=0;
      const groups=new Map();
      for(const state of states){
        const quantity=Math.max(0,Math.round(Number(state.quantity)||0)),price=Math.max(0,Math.round(Number(state.price)||0));
        const pid=String(state.productId);
        subtotal+=price*quantity;
        if(!priceRules[pid])continue;
        eligibleCount+=quantity;eligibleSubtotal+=price*quantity;
        const group=groups.get(pid)||{quantity:0,price};group.quantity+=quantity;groups.set(pid,group);
      }
      let quantitySaving=0;
      for(const [pid,group] of groups)quantitySaving+=quoteProduct(group.price,group.quantity,priceRules[pid].rules);
      const tiers=mixed?.tiers||[];
      const rule=tiers.filter(r=>eligibleCount>=Number(r.min)).sort((a,b)=>Number(b.min)-Number(a.min))[0];
      const mixedSaving=rule?Math.min(eligibleSubtotal,Number(rule.saving)||0):0;
      const saving=Math.min(subtotal,Math.max(quantitySaving,mixedSaving));
      return {subtotal,saving,total:subtotal-saving};
    };
    const selectionQuote=(states,priceRules,existing=[],mixed)=>{
      const before=cartQuote(existing,priceRules,mixed),after=cartQuote([...existing,...states],priceRules,mixed);
      const subtotal=states.reduce((sum,s)=>sum+(Number(s.price)||0)*(Number(s.quantity)||0),0);
      const saving=Math.max(0,Math.min(subtotal,after.saving-before.saving));
      return {subtotal,saving,total:subtotal-saving,cartTotal:after.total};
    };
    let pricing={products:{},mixed:{tiers:[]}};
    let pricingAvailable=false;
    try{
      const response=await fetch(root.dataset.pricingUrl,{credentials:'same-origin',cache:'no-store'});
      if(!response.ok)throw Error('Pricing configuration unavailable');
      const data=await response.json();
      if(!data?.products||typeof data.products!=='object')throw Error('Invalid pricing configuration');
      pricing=data;pricingAvailable=true;
    }catch(e){
      // Products and canonical variants still work when the estimate cannot be fetched.
      // Do not quote speculative savings; Shopify remains authoritative at checkout.
      console.warn('[ZYMA BYOB] Pricing preview unavailable; checkout calculates actual savings.',e);
    }
    const cards = [...root.querySelectorAll('[data-builder-product]')];
    const selectedList = root.querySelector('[data-builder-selected]');
    const progress = root.querySelector('[data-builder-progress]');
    const primaryCta = root.querySelector('[data-builder-submit]');
    const mobileCta = root.querySelector('[data-builder-mobile-submit]');
    const status = root.querySelector('[data-builder-status]');
    const minItems = Number(root.dataset.minItems) || 2;
    const maxItems = Number(root.dataset.maxItems) || 3;
    const maxPerProduct = Number(root.dataset.maxPerProduct) || 3;
    const shippingThreshold = Math.max(0, Number(root.dataset.shippingThreshold) || 0);
    let existingStates = [];
    let currentCartTotal = 0;
    let cartReady = false;
    let cartRevision = 0;
    let busy = false;
    // Ambiguous network response after POST: require cart confirmation before any resubmit.
    let needsCartReview = false;
    const formatMoney = cents => {
      const value = Math.max(0, Math.round(Number(cents) || 0));
      const decimals = value % 100 === 0 ? 0 : 2;
      const number = (thousands, decimal, places = decimals) => {
        const [whole, fraction] = (value / 100).toFixed(places).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      const formats = {
        amount: () => number(',', '.'), amount_no_decimals: () => number(',', '.', 0),
        amount_with_comma_separator: () => number('.', ','), amount_no_decimals_with_comma_separator: () => number('.', ',', 0),
        amount_with_apostrophe_separator: () => number("'", '.'), amount_no_decimals_with_space_separator: () => number(' ', ',', 0),
        amount_with_space_separator: () => number(' ', ',')
      };
      return (root.dataset.moneyFormat || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
    };
    const cardState = card => {
      const select = card.querySelector('[data-builder-variant]');
      const selected = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
      return {
        card, variantId: selected?.value || card.dataset.variantId || '',
        price: Number(selected?.dataset.price || card.dataset.price || 0),
        available: (selected?.dataset.available || card.dataset.available || 'true') !== 'false',
        quantity: Math.max(0, Math.round(Number(card.dataset.quantity) || 0)),
        title: card.dataset.title || '', productId: card.dataset.productId || '',
        productHandle: card.dataset.productHandle || ''
      };
    };
    const allStates = () => cards.map(cardState);
    const quoteFor = states => selectionQuote(states, pricing.products, existingStates, pricing.mixed);
    const showStatus = message => { if (status) status.textContent = message || ''; };
    const setCart = cart => {
      existingStates = (cart.items || []).map(item => ({ productId: String(item.product_id), variantId: String(item.variant_id), price: Number(item.original_price ?? item.price), quantity: Number(item.quantity) }));
      currentCartTotal = Number(cart.total_price) || 0;
      cartReady = true;
    };
    const getCart = async () => {
      const response = await fetch(rootUrl() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(text('Error'));
      return response.json();
    };
    const render = () => {
      const states = allStates();
      const count = states.reduce((sum, state) => sum + state.quantity, 0);
      const quote = quoteFor(states);
      const setText = (selector, value) => root.querySelectorAll(selector).forEach(node => { node.textContent = value; });
      setText('[data-builder-count]', String(count));
      setText('[data-builder-subtotal]', formatMoney(quote.subtotal));
      setText('[data-builder-saving]', pricingAvailable ? (quote.saving > 0 ? '−' + formatMoney(quote.saving) : formatMoney(0)) : (document.documentElement.lang.startsWith('ar')?'عند الدفع':'At checkout'));
      setText('[data-builder-total]', formatMoney(quote.total));
      if (progress) progress.textContent = !pricingAvailable ? (document.documentElement.lang.startsWith('ar')?'اختار عبوتين أو 3؛ الخصم النهائي بيتحسب في السلة.':'Choose 2 or 3 packs; your final eligible discount appears in the bag.') : count < minItems ? fill(text('ProgressEmpty'), { count, remaining: minItems - count, min:minItems }) : quote.saving > 0 ? fill(text(count === 2 ? 'ProgressPair' : 'ProgressUnlocked'), { saving: formatMoney(quote.saving), count, remaining: Math.max(0, minItems - count) }) : text('ProgressNoSaving');
      root.querySelector('[data-builder-meter]')?.style.setProperty('--byob-meter', Math.min(100, count / maxItems * 100) + '%');
      if (shippingThreshold > 0) {
        const projectedTotal = currentCartTotal + quote.total;
        const remaining = Math.max(0, shippingThreshold - projectedTotal);
        root.querySelector('[data-builder-shipping-wrap]')?.classList.toggle('is-unlocked', remaining <= 0);
        setText('[data-builder-shipping]', remaining <= 0 ? text('ShippingUnlocked') : fill(text('ShippingProgress'), { amount: formatMoney(remaining) }));
        const meter = root.querySelector('[data-builder-shipping-meter]');
        if (meter) meter.style.width = Math.min(100, projectedTotal / shippingThreshold * 100) + '%';
      }
      if (selectedList) selectedList.replaceChildren(...states.filter(state => state.quantity > 0).map(state => {
        const row = document.createElement('li');
        row.className = 'zyma-byob__selected-row';
        const name = document.createElement('span');
        name.textContent = state.title + ' ×' + state.quantity;
        const price = document.createElement('span');
        price.textContent = formatMoney(state.price * state.quantity);
        row.append(name, price);
        return row;
      }));
      for (const card of cards) {
        const state = cardState(card);
        card.classList.toggle('is-selected', state.quantity > 0);
        const add = card.querySelector('[data-builder-add]');
        const step = card.querySelector('[data-builder-step]');
        const qty = card.querySelector('[data-builder-qty]');
        if (add) add.hidden = state.quantity > 0;
        if (step) step.hidden = state.quantity <= 0;
        if (qty) qty.textContent = String(state.quantity);
        const minus = card.querySelector('[data-builder-minus]');
        const plus = card.querySelector('[data-builder-plus]');
        if (minus) minus.disabled = state.quantity <= 0 || busy || needsCartReview;
        if (plus) plus.disabled = !state.available || busy || needsCartReview || state.quantity >= maxPerProduct || count >= maxItems;
        if (add) add.disabled = !state.available || busy || needsCartReview || count >= maxItems;
      }
      const disabled = busy || needsCartReview || !cartReady || (count < minItems || count > maxItems) || states.some(state => state.quantity > 0 && (!state.available || !/^[0-9]+$/.test(state.variantId)));
      const values = { min: minItems, total: formatMoney(quote.total), count };
      if (primaryCta) {
        primaryCta.disabled = disabled;
        primaryCta.textContent = busy ? text('CtaBusy') : fill(count === 2 ? text('CtaPair') : count >= minItems ? text('CtaReady') : text('CtaIdle'), values);
      }
      if (mobileCta) {
        mobileCta.disabled = disabled;
        mobileCta.textContent = busy ? text('CtaBusy') : fill(count === 2 ? text('MobilePair') : count >= minItems ? text('MobileReady') : text('MobileIdle'), values);
      }
    };
    const refreshCart = async () => {
      const revision = ++cartRevision;
      try {
        const cart = await getCart();
        if (revision !== cartRevision) return;
        setCart(cart);
        showStatus('');
        render();
      } catch (error) {
        if (!cartReady) {
          console.warn('[ZYMA BYOB] Cannot read cart:',error);
          showStatus(document.documentElement.lang.startsWith('ar') ? 'محتاجين نحدّث السلة قبل الإضافة.' : 'Please refresh your bag before adding.');
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'zyma-byob__retry';
          retry.textContent = document.documentElement.lang.startsWith('ar') ? 'تحديث السلة' : 'Refresh bag';
          retry.addEventListener('click', () => { retry.disabled = true; refreshCart(); });
          status?.append(' ', retry);
        }
      }
    };
    const setQuantity = (card, next) => {
      if (busy || needsCartReview) return;
      const otherCount = allStates().filter(state => state.card !== card).reduce((sum, state) => sum + state.quantity, 0);
      const quantity = Math.max(0, Math.min(maxPerProduct, maxItems - otherCount, Math.round(Number(next) || 0)));
      card.dataset.quantity = String(quantity);
      showStatus('');
      render();
    };
    for (const card of cards) {
      card.querySelector('[data-builder-add]')?.addEventListener('click', () => setQuantity(card, 1));
      card.querySelector('[data-builder-minus]')?.addEventListener('click', () => setQuantity(card, Number(card.dataset.quantity || 0) - 1));
      card.querySelector('[data-builder-plus]')?.addEventListener('click', () => setQuantity(card, Number(card.dataset.quantity || 0) + 1));
      card.querySelector('[data-builder-variant]')?.addEventListener('change', render);
    }
    root.querySelectorAll('[data-builder-dialog-open]').forEach(button => button.addEventListener('click', () => {
      const dialog = document.getElementById(button.dataset.builderDialogOpen || '');
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
    }));
    root.querySelectorAll('[data-builder-dialog-close]').forEach(button => button.addEventListener('click', () => button.closest('dialog')?.close()));
    root.querySelectorAll('dialog[data-builder-dialog]').forEach(dialog => dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }));
    const sectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const openDrawer = async () => {
      const cartFallback = () => window.location.assign(rootUrl() + 'cart');
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) { cartFallback(); return; }
      if (!customElements.get('cart-drawer-component')) {
        // A failed or delayed module must not strand the buyer after a confirmed add.
        await Promise.race([
          customElements.whenDefined('cart-drawer-component'),
          new Promise(resolve => setTimeout(resolve, 1800))
        ]);
      }
      if (typeof drawer.open === 'function') await drawer.open();
      else cartFallback();
    };
    const submit = async () => {
      if (busy || needsCartReview || !cartReady) return;
      const states = allStates().filter(state => state.quantity > 0);
      const selectionCount = states.reduce((sum, state) => sum + state.quantity, 0);
      if (selectionCount < minItems || selectionCount > maxItems || states.some(state=>!state.available||!/^[0-9]+$/.test(state.variantId))) return;
      busy = true;
      showStatus('');
      render();
      let addConfirmed = false;
      let postAttempted = false;
      let explicitRejection = false;
      let beforeCartSnapshot = null;
      try {
        const beforeCart = await getCart();
        beforeCartSnapshot = beforeCart;
        setCart(beforeCart);
        const expected = quoteFor(states);
        const payload = {
          items: states.map(state => ({ id: Number(state.variantId), quantity: state.quantity })),
          sections_url: window.location.pathname
        };
        const ids = sectionIds();
        if (ids.length) payload.sections = ids.join(',');
        postAttempted = true;
        const response = await fetch(rootUrl() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.status) {
          // A deliberate Shopify 4xx rejection is different from a lost HTTP response.
          explicitRejection = response.status >= 400 && response.status < 500;
          throw new Error(data?.description || text('Error'));
        }
        addConfirmed = true;
        // Never submit the same selection again merely because the follow-up cart read failed.
        const cart = await getCart();
        setCart(cart);
        const sections = data.sections || {};
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { resource: { ...cart, sections }, sourceId: 'zyma-bundle-builder', data: { source: 'zyma-bundle-builder', itemCount: Number(cart.item_count) || 0, sections } }
        }));
        const actualDelta = Number(cart.total_price) - Number(beforeCart.total_price);
        track('byob_completed', { zyma_item_count: states.reduce((sum, state) => sum + state.quantity, 0), zyma_distinct_products: states.length, zyma_total_cents: actualDelta, zyma_expected_total_cents: expected.total });
        cards.forEach(card => { card.dataset.quantity = '0'; });
        showStatus(fill(text(states.reduce((sum, state) => sum + state.quantity, 0) === 2 ? 'StatusPair' : 'StatusAdded'), { count: states.reduce((sum, state) => sum + state.quantity, 0) }));
        render();
        await openDrawer();
      } catch (error) {
        if (addConfirmed) {
          showStatus(fill(text(states.reduce((sum, state) => sum + state.quantity, 0) === 2 ? 'StatusPair' : 'StatusAdded'), { count: states.reduce((sum, state) => sum + state.quantity, 0) }));
          cards.forEach(card => { card.dataset.quantity = '0'; });
          // The POST succeeded. Open the canonical cart instead of presenting a false add error.
          window.location.assign(rootUrl() + 'cart');
        } else if (postAttempted && beforeCartSnapshot && !explicitRejection) {
          // A failed network response does not prove Shopify rejected the POST.
          // Reconcile exact canonical variant deltas before ever allowing another attempt.
          let confirmedCart = null;
          try {
            const after = await getCart();
            const countVariant = (cart, id) => (cart.items || []).reduce(
              (sum, line) => sum + (String(line.variant_id) === String(id) ? Number(line.quantity) || 0 : 0), 0
            );
            const fullyPresent = states.every(state => (
              countVariant(after, state.variantId) - countVariant(beforeCartSnapshot, state.variantId) >= state.quantity
            ));
            if (fullyPresent) confirmedCart = after;
          } catch {
            // Network is still unavailable: the result is unknown, not a proven failure.
          }
          if (confirmedCart) {
            setCart(confirmedCart);
            cards.forEach(card => { card.dataset.quantity = '0'; });
            showStatus(fill(text(states.reduce((sum, state) => sum + state.quantity, 0) === 2 ? 'StatusPair' : 'StatusAdded'), { count: states.reduce((sum, state) => sum + state.quantity, 0) }));
            document.dispatchEvent(new CustomEvent('cart:update', {
              detail: { resource: confirmedCart, sourceId: 'zyma-bundle-builder', data: { source: 'zyma-bundle-builder', itemCount: Number(confirmedCart.item_count) || 0 } }
            }));
            try { await openDrawer(); } catch { window.location.assign(rootUrl() + 'cart'); }
          } else {
            needsCartReview = true;
            const ar = document.documentElement.lang.startsWith('ar');
            showStatus(ar
              ? 'مش قادرين نتأكد إن العبوات اتضافت. راجع سلتك قبل ما تحاول تاني عشان ميتضافوش مرتين.'
              : 'We could not confirm whether the items were added. Check your bag before trying again to prevent duplicates.');
            const link = document.createElement('a');
            link.href = rootUrl() + 'cart';
            link.className = 'zyma-byob__review-cart';
            link.textContent = ar ? 'راجع السلة' : 'Review bag';
            status?.append(' ', link);
          }
        } else {
          const message = error instanceof Error ? error.message : '';
          showStatus(document.documentElement.lang.startsWith('ar') && !/[\u0600-\u06FF]/.test(message) ? text('Error') : message || text('Error'));
        }
      } finally {
        busy = false;
        render();
      }
    };
    primaryCta?.addEventListener('click', submit);
    mobileCta?.addEventListener('click', submit);
    document.addEventListener('cart:update', refreshCart);
    render();
    await refreshCart();
  };
  const start = root => init(root).catch((error) => {
    console.error('[ZYMA BYOB] Initialization failed',error);
    root.dataset.zymaBuilderReady = 'false';
    const status = root.querySelector('[data-builder-status]');
    if (!status) return;
    status.textContent = document.documentElement.lang.startsWith('ar') ? 'في مشكلة مؤقتة في تحميل سلة التجميع.' : 'Bundle builder could not load. Please retry.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'zyma-byob__retry';
    retry.textContent = document.documentElement.lang.startsWith('ar') ? 'إعادة المحاولة' : 'Retry';
    retry.addEventListener('click', () => { retry.disabled = true; status.textContent=''; start(root); });
    status.append(' ', retry);
  });
  const initAll = (scope = document) => scope.querySelectorAll?.('[data-zyma-bundle-builder]').forEach(start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();

